'use client';
import { Search, X } from 'lucide-react';
import type { ChangeEvent, ComponentProps } from 'react';

import { IconTouchable } from '@vs-shop/ui/iconTouchable';
import { Input } from '@vs-shop/ui/input';
import { clsxm } from '@vs-shop/utils';

/** The field's box, holding the room the adornment overlays. */
const filterRailSearchClasses = 'pb-2';

/** The positioning context the adornment hangs in. */
const filterRailSearchFieldClasses = 'relative';

/** The trailing mark: a magnifier while the field is empty, a clear control once it is not. */
const filterRailSearchAdornmentClasses = 'absolute top-1/2 right-3 -translate-y-1/2';

export type FilterRailSearchProps = Omit<
  ComponentProps<'div'>,
  'children' | 'id' | 'onChange' | 'defaultValue'
> & {
  /**
   * The field's own `id`, which its label points at. A prop rather than a
   * literal: a listing renders one rail per facet plus a second copy inside the
   * mobile drawer, and a name typed into this file would be the same `id` on
   * every one of them.
   */
  readonly id: string;
  readonly value: string;
  /** Called with the field's new text on every keystroke, and with `''` on clear. */
  readonly onValueChange: (value: string) => void;
  /** The field's accessible name. */
  readonly label: string;
  /** What the control that empties the field is called. */
  readonly clearLabel: string;
  readonly placeholder?: string;
};

/**
 * The field that narrows a long facet to what someone is looking for.
 *
 * A client leaf: it swaps its own adornment and writes every keystroke straight
 * out. The query itself is the caller's state, so the facet's parts around it
 * stay server-rendered and the rail has no client root.
 *
 * The field schedules no deferred write. A deferred write carries the text as
 * it stood when the timer started, so it lands after a later change and undoes
 * it: a clear inside the window came back as the query that was just cleared.
 */
export const FilterRailSearch = ({
  className,
  id,
  value,
  onValueChange,
  label,
  clearLabel,
  placeholder,
  ...props
}: FilterRailSearchProps) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onValueChange(event.target.value);
  };

  return (
    <div
      data-slot="filter-rail-search"
      className={clsxm(filterRailSearchClasses, className)}
      {...props}
    >
      <div data-slot="filter-rail-search-field" className={filterRailSearchFieldClasses}>
        <Input
          id={id}
          type="text"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          label={label}
          className="h-12 pr-9"
          enableCheck={false}
        />
        <span data-slot="filter-rail-search-adornment" className={filterRailSearchAdornmentClasses}>
          {value ? (
            <IconTouchable onClick={() => onValueChange('')} aria-label={clearLabel}>
              <X className="size-4 shrink-0" />
            </IconTouchable>
          ) : (
            <Search className="text-primary-intense size-4 shrink-0" />
          )}
        </span>
      </div>
    </div>
  );
};
